// Product page functions

// DOM element cache for performance optimization
const DOM_CACHE = {
  shareButtons: null,
  addToCartButton: null,
  floatingCta: null,
};

// Cache DOM elements on first access
const getCachedElement = (key, selector) => {
  if (!DOM_CACHE[key]) {
    DOM_CACHE[key] = document.querySelector(selector);
  }
  return DOM_CACHE[key];
};

const getCachedElements = (key, selector) => {
  if (!DOM_CACHE[key]) {
    DOM_CACHE[key] = document.querySelectorAll(selector);
  }
  return DOM_CACHE[key];
};

/**
 * Initializes the share functionality using the Web Share API
 * Allows sharing the current page through the device's native share menu
 */
const initShareButton = () => {
  const shareButtons = getCachedElements("shareButtons", ".js-share-button");

  if (!shareButtons.length) return;

  // Check if the browser supports the Web Share API
  const canShare = navigator?.share !== undefined;

  shareButtons.forEach((button) => {
    // If the browser doesn't support sharing, hide the button
    if (!canShare) {
      button.style.display = "none";
      return;
    }

    button.addEventListener("click", async (event) => {
      event.preventDefault();

      try {
        // Sanitize and validate share data
        const url = button.dataset.shareUrl?.trim() || window.location.href;
        const title = button.dataset.shareTitle?.trim() || document.title;
        const text = button.dataset.shareText?.trim() || "";

        // Validate URL format for security
        if (!url.startsWith("http://") && !url.startsWith("https://")) {
          throw new Error("Invalid URL format");
        }

        // Invoke the native share menu
        await navigator.share({ title, text, url });
        if (window.analyticsUtils && window.analyticsUtils.handleProductShare) {
          window.analyticsUtils.handleProductShare(undefined);
        }
      } catch (error) {
        // Only log non-user-cancelled errors
        if (error.name !== "AbortError") {
          console.error("Error sharing:", error);
        }
      }
    });
  });
};

// Add to cart button observer for floating CTA
const initFloatingCtaObserver = () => {
  const addToCartButton = getCachedElement(
    "addToCartButton",
    ".product-form__submit",
  );
  const floatingCta = getCachedElement("floatingCta", ".floating-cta");

  if (!addToCartButton || !floatingCta) {
    console.warn("Elements .product-form__submit or .floating-cta not found");
    return;
  }

  const ctaObserver = new IntersectionObserver(
    (entries) => {
      const [entry] = entries;
      floatingCta.classList.toggle("hidden", entry.isIntersecting);
    },
    { threshold: 0, rootMargin: "0px" },
  );

  ctaObserver.observe(addToCartButton);
};

// Simplified Swiper initialization for synchronous product recommendations
const initCompleteLookSwiper = () => {
  const carouselElement = document.querySelector("#completeLookCarousel");

  if (!carouselElement) {
    console.warn("Complete look carousel not found");
    return;
  }

  const slides = carouselElement.querySelectorAll(".swiper-slide");

  if (slides.length === 0) {
    console.warn("No products found for complete look carousel");
    return;
  }

  try {
    new Swiper(carouselElement, {
      slidesPerView: 2.75,
      spaceBetween: 4,
      slidesOffsetBefore: 56,
      slidesOffsetAfter: 56,
      navigation: {
        nextEl: ".brn-carousel__nav--next",
        prevEl: ".brn-carousel__nav--prev",
      },
      breakpoints: {
        1024: {
          slidesPerView: 6.75,
        },
      },
    });
  } catch (error) {
    console.error("Error initializing complete look Swiper:", error);
  }
};

// Size chart helpers (initSizeChartTabs / initSizeChartTabsFromSource /
// buildSizeChartPanesFromSource) now live in assets/size-chart.js, loaded
// globally and exposed as window.sizeChartHelpers.

/**
 * Copies the product reference (SKU) to the clipboard from the "Ver detalles"
 * modal, giving brief visual/aria feedback on success.
 */
const initReferenceCopy = () => {
  const copyButtons = document.querySelectorAll(".js-copy-reference");

  if (!copyButtons.length) return;

  copyButtons.forEach((button) => {
    button.addEventListener("click", async () => {
      const value = button.dataset.copyValue;
      if (!value) return;

      try {
        await navigator.clipboard.writeText(value);
      } catch (error) {
        console.error("Error copying reference:", error);
        return;
      }

      button.classList.add("is-copied");
      button.setAttribute("aria-label", button.dataset.labelCopied || button.dataset.labelCopy);

      window.clearTimeout(button._copyResetTimeout);
      button._copyResetTimeout = window.setTimeout(() => {
        button.classList.remove("is-copied");
        button.setAttribute("aria-label", button.dataset.labelCopy);
      }, 2000);
    });
  });
};

// Initialize all product page functionality
/**
 * Simple loader management for recommendations
 * Shows loader by default, hides it when .brn-carousel appears
 */

document.addEventListener("DOMContentLoaded", () => {
  initShareButton();
  initReferenceCopy();
  initFloatingCtaObserver();
  initCompleteLookSwiper();
  window.sizeChartHelpers?.initSizeChartTabsFromSource();
  window.sizeChartHelpers?.buildSizeChartPanesFromSource();
  window.sizeChartHelpers?.initSizeChartTabs();
  window.sizeChartHelpers?.initSizeGuideDiagramAnimation();
});
